import { useMutation } from '@tanstack/react-query'
import { ContentAction } from '@today/types'
import type { ApproveDialogViewProps, ApproveValuesView, ReasonDialogViewProps } from '@today/ui-domain'
import { useState } from 'react'
import { contentService } from '@/services/content.service'
import { contentErrorStatus } from './content-ui'
import { actionBody, approveBody, approveErrors, reasonError } from './review-rules'
import { useContentT } from './useContentQueries'

/** The reason-only review actions; "pull back" is a reject by the author. */
export type ReasonAction = 'reject' | 'pullBack' | 'retire'

type Target = { id: string; kind: 'approve' | ReasonAction }

const EMPTY_APPROVE: ApproveValuesView = { reason: '', validFrom: '', validTo: '' }
const APPROVE_FIELDS = new Set(['reason', 'validFrom', 'validTo'])

const TITLE: Record<ReasonAction, string> = {
	reject: 'review.rejectTitle',
	pullBack: 'review.pullBackTitle',
	retire: 'review.retireTitle',
}

/** Dialog target and inputs, reset on every open. */
function useReviewState() {
	const [target, setTarget] = useState<Target | null>(null)
	const [values, setValues] = useState<ApproveValuesView>(EMPTY_APPROVE)
	const [reasonText, setReasonText] = useState('')
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [serverError, setServerError] = useState<string | null>(null)
	const open = (next: Target) => {
		setTarget(next)
		setValues(EMPTY_APPROVE)
		setReasonText('')
		setErrors({})
		setServerError(null)
	}
	return {
		target,
		setTarget,
		values,
		setValues,
		reasonText,
		setReasonText,
		errors,
		setErrors,
		serverError,
		setServerError,
		open,
	}
}

type ReviewState = ReturnType<typeof useReviewState>

/** The action call: close + reload on 200; field or dialog message on a refusal; reload on 409. */
function useAct(state: ReviewState, onDone: () => Promise<unknown> | undefined) {
	const { fieldError } = useContentT()
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => contentService.act(id, body),
		onSuccess: async () => {
			state.setTarget(null)
			await onDone()
		},
		onError: (error) => {
			const { field, message } = fieldError(error)
			if (APPROVE_FIELDS.has(field)) state.setErrors({ [field]: message })
			else state.setServerError(message)
			// A conflict means the status moved under us: reload what the page shows.
			if (contentErrorStatus(error) === 409) void onDone()
		},
	})
}

/** Check the dialog input client-side, then send the action. */
function confirmers(state: ReviewState, act: ReturnType<typeof useAct>, t: (key: string) => string) {
	const { target, values, reasonText } = state
	const kind = target?.kind
	return {
		confirmApprove: () => {
			const found = approveErrors(values)
			state.setErrors(Object.fromEntries(Object.entries(found).map(([f, key]) => [f, t(key)])))
			state.setServerError(null)
			if (target && Object.keys(found).length === 0) act.mutate({ id: target.id, body: approveBody(values) })
		},
		confirmReason: () => {
			const key = reasonError(reasonText)
			state.setErrors(key ? { reason: t(key) } : {})
			state.setServerError(null)
			if (!target || key || !kind || kind === 'approve') return
			act.mutate({
				id: target.id,
				body: actionBody(kind === 'retire' ? ContentAction.RETIRE : ContentAction.REJECT, reasonText),
			})
		},
	}
}

/**
 * Approve / reject / pull back / retire dialogs of one localization at a time
 * (TO-420 content-admin-ui REQ-005). Nothing is applied optimistically: the
 * dialog closes and `onDone` reloads only after the server answers 200.
 */
export function useReviewDialogs(onDone: () => Promise<unknown> | undefined): {
	openApprove: (localizationId: string) => void
	openReason: (localizationId: string, kind: ReasonAction) => void
	approve: ApproveDialogViewProps
	reason: ReasonDialogViewProps
} {
	const { t } = useContentT()
	const state = useReviewState()
	const act = useAct(state, onDone)
	const { target, values, reasonText } = state
	const kind = target?.kind
	const { confirmApprove, confirmReason } = confirmers(state, act, t)
	const reasonKind: ReasonAction = kind && kind !== 'approve' ? kind : 'reject'
	const close = () => state.setTarget(null)
	return {
		openApprove: (id) => state.open({ id, kind: 'approve' }),
		openReason: (id, reasonAction) => state.open({ id, kind: reasonAction }),
		approve: {
			t,
			isOpen: kind === 'approve',
			values,
			errors: state.errors,
			error: state.serverError,
			isBusy: act.isPending,
			onChange: (field, value) => state.setValues((v) => ({ ...v, [field]: value })),
			onConfirm: confirmApprove,
			onCancel: close,
		},
		reason: {
			t,
			isOpen: kind !== undefined && kind !== 'approve',
			title: t(TITLE[reasonKind]),
			confirmLabel: t(`lifecycle.${reasonKind}`),
			reason: reasonText,
			reasonError: state.errors.reason,
			error: state.serverError,
			isBusy: act.isPending,
			onReasonChange: state.setReasonText,
			onConfirm: confirmReason,
			onCancel: close,
		},
	}
}
